class CategoryProductSlider {
  constructor() {
    this.initializeVariables();
    this.initSliders();
    this.randomizeProducts();
    this.bindEvents();
    this.setupResizeObserver();
    this.setupColorVariables();
    this.setupImageHandling();
  }

  initializeVariables() {
    this.config = {
      scrollBehavior: "smooth",
      animationDuration: 300,
      mobileBreakpoint: 768,
      tabletBreakpoint: 1024,
      preloadCount: 3,
      touchThreshold: 50,
      scrollStepDesktop: 350,
      scrollStepMobile: 260,
      debounceDelay: 150,
      loadingDelay: 100,
    };
  }

  initSliders() {
    this.sliders = document.querySelectorAll(".category-product-slider");
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.touchStartY = 0;
    this.isScrolling = false;
    this.scrollTimeout = null;
    this.resizeTimeout = null;
    this.imageLoadTimeout = null;
    this.scrollStep =
      window.innerWidth < this.config.mobileBreakpoint
        ? this.config.scrollStepMobile
        : this.config.scrollStepDesktop;
  }

  randomizeProducts() {
    this.sliders.forEach((slider) => {
      const container = slider.querySelector(
        '.slider-container[data-random-products="true"]'
      );
      if (!container) return;

      const maxProducts = parseInt(slider.dataset.maxProducts) || 6;
      const productItems = Array.from(
        container.querySelectorAll(".product-item")
      );

      // Fisher-Yates shuffle algorithm
      for (let i = productItems.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        // Remove existing animation delay
        productItems[i].style.setProperty("--animation-delay", "0s");
        productItems[j].style.setProperty("--animation-delay", "0s");
        // Swap elements
        container.appendChild(productItems[j]);
      }

      // Keep only the required number of products
      while (container.children.length > maxProducts) {
        container.removeChild(container.lastChild);
      }

      // Add new staggered animation delays
      container.querySelectorAll(".product-item").forEach((item, index) => {
        item.style.setProperty("--animation-delay", `${index * 0.1}s`);
      });
    });
  }

  setupImageHandling() {
    this.sliders.forEach((slider) => {
      const images = slider.querySelectorAll("img");
      images.forEach((img) => this.handleImageLoading(img));
    });
  }

  handleImageLoading(img) {
    const wrapper = img.closest(".image-wrapper, .image-container");
    if (!wrapper) return;

    wrapper.classList.add("loading");

    const loadImage = () => {
      wrapper.classList.remove("loading");
      wrapper.classList.add("loaded");

      img.style.opacity = "0";
      requestAnimationFrame(() => {
        img.style.transition = "opacity 0.3s ease";
        img.style.opacity = "1";
      });
    };

    if (img.complete) {
      loadImage();
    } else {
      img.addEventListener("load", loadImage);
      img.addEventListener("error", () => {
        wrapper.classList.remove("loading");
        wrapper.classList.add("error");
      });
    }
  }

  setupColorVariables() {
    this.sliders.forEach((slider) => {
      const buttonColor =
        slider.querySelector(".category-button")?.dataset?.buttonColor ||
        "#ff8c00";
      const buttonHoverColor =
        slider.querySelector(".category-button")?.dataset?.buttonHoverColor ||
        "#ff4500";

      slider.style.setProperty("--button-color", buttonColor);
      slider.style.setProperty("--button-hover-color", buttonHoverColor);
    });
  }

  bindEvents() {
    // Arrow button clicks
    document.addEventListener("click", this.handleArrowClick.bind(this));

    // Initialize each slider
    this.sliders.forEach((slider) => {
      const container = slider.querySelector(".slider-container");

      if (container) {
        // Touch events
        container.addEventListener(
          "touchstart",
          this.handleTouchStart.bind(this),
          { passive: true }
        );
        container.addEventListener(
          "touchmove",
          this.handleTouchMove.bind(this),
          { passive: true }
        );
        container.addEventListener("touchend", this.handleTouchEnd.bind(this), {
          passive: true,
        });

        // Scroll events
        container.addEventListener("scroll", this.handleScroll.bind(this), {
          passive: true,
        });

        // Initial arrow states
        const productSlider = container.closest(".product-slider");
        if (productSlider) {
          this.updateArrowStates(productSlider);
        }
      }
    });

    // Keyboard navigation
    document.addEventListener("keydown", this.handleKeyboard.bind(this));
  }

  handleArrowClick(event) {
    const arrow = event.target.closest(".slider-arrow");
    if (!arrow) return;

    const slider = arrow.closest(".product-slider");
    const container = slider.querySelector(".slider-container");
    const direction = arrow.classList.contains("left-arrow") ? -1 : 1;

    this.scrollContainer(container, direction);
    event.preventDefault();
  }

  handleScroll(event) {
    if (this.scrollTimeout) {
      window.clearTimeout(this.scrollTimeout);
    }

    this.scrollTimeout = window.setTimeout(() => {
      const productSlider = event.target.closest(".product-slider");
      if (productSlider) {
        this.updateArrowStates(productSlider);
      }
    }, this.config.debounceDelay);
  }

  scrollContainer(container, direction) {
    if (this.isScrolling) return;

    this.isScrolling = true;
    const slider = container.closest(".product-slider");

    const scrollAmount =
      this.scrollStep +
      (window.innerWidth < this.config.mobileBreakpoint ? 15 : 25); // Include gap

    try {
      container.scrollBy({
        left: scrollAmount * direction,
        behavior: this.config.scrollBehavior,
      });

      // Haptic feedback on mobile
      if (window.navigator.vibrate) {
        window.navigator.vibrate(50);
      }
    } catch (error) {
      // Fallback for browsers that don't support smooth scrolling
      container.scrollLeft += scrollAmount * direction;
    }

    setTimeout(() => {
      this.isScrolling = false;
      this.updateArrowStates(slider);
    }, this.config.animationDuration);
  }

  updateArrowStates(slider) {
    const container = slider.querySelector(".slider-container");
    const leftArrow = slider.querySelector(".left-arrow");
    const rightArrow = slider.querySelector(".right-arrow");

    if (!container || !leftArrow || !rightArrow) return;

    const isAtStart = Math.abs(container.scrollLeft) < 1;
    const isAtEnd =
      Math.abs(
        container.scrollLeft + container.clientWidth - container.scrollWidth
      ) < 1;

    leftArrow.classList.toggle("disabled", isAtStart);
    rightArrow.classList.toggle("disabled", isAtEnd);
    leftArrow.setAttribute("aria-disabled", isAtStart);
    rightArrow.setAttribute("aria-disabled", isAtEnd);
  }

  handleTouchStart(event) {
    this.touchStartX = event.touches[0].clientX;
    this.touchStartY = event.touches[0].clientY;
    this.isScrolling = false;
  }

  handleTouchMove(event) {
    if (!this.touchStartX) return;

    const touchCurrentX = event.touches[0].clientX;
    const touchCurrentY = event.touches[0].clientY;
    const deltaX = this.touchStartX - touchCurrentX;
    const deltaY = this.touchStartY - touchCurrentY;

    if (!this.isScrolling) {
      this.isScrolling = Math.abs(deltaX) > Math.abs(deltaY);
    }
  }

  handleTouchEnd(event) {
    if (!this.isScrolling) return;

    this.touchEndX = event.changedTouches[0].clientX;
    const touchDiff = this.touchStartX - this.touchEndX;
    const container = event.target.closest(".slider-container");
    const slider = container.closest(".product-slider");

    if (Math.abs(touchDiff) > this.config.touchThreshold) {
      const direction = touchDiff > 0 ? 1 : -1;
      this.scrollContainer(container, direction);
    }

    this.updateArrowStates(slider);
    this.touchStartX = null;
    this.touchEndX = null;
    this.isScrolling = false;
  }

  handleKeyboard(event) {
    const activeSlider = document.querySelector(
      ".category-product-slider:hover"
    );
    if (!activeSlider) return;

    const container = activeSlider.querySelector(".slider-container");
    if (!container) return;

    switch (event.key) {
      case "ArrowLeft":
        this.scrollContainer(container, -1);
        event.preventDefault();
        break;
      case "ArrowRight":
        this.scrollContainer(container, 1);
        event.preventDefault();
        break;
    }
  }

  setupResizeObserver() {
    window.addEventListener(
      "resize",
      () => {
        if (this.resizeTimeout) {
          window.clearTimeout(this.resizeTimeout);
        }

        this.resizeTimeout = window.setTimeout(() => {
          this.scrollStep =
            window.innerWidth < this.config.mobileBreakpoint
              ? this.config.scrollStepMobile
              : this.config.scrollStepDesktop;

          this.sliders.forEach((slider) => {
            const productSlider = slider.querySelector(".product-slider");
            if (productSlider) {
              this.updateArrowStates(productSlider);
            }
          });
        }, this.config.debounceDelay);
      },
      { passive: true }
    );
  }
}

// Initialize when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  new CategoryProductSlider();
});

// Reinitialize on Shopify section load
document.addEventListener("shopify:section:load", () => {
  new CategoryProductSlider();
});

// Handle Shopify theme editor events
document.addEventListener("shopify:section:select", (event) => {
  if (event.target.classList.contains("category-product-slider")) {
    new CategoryProductSlider();
  }
});
